/* ==========================================================================
   VYORO B2B — Core theme JavaScript
   Vanilla ES2017+. No framework, no external dependencies.
   Provides: shared utilities, overlay controller, header behaviour,
   navigation, reveal animations, accordions, tabs, counters.
   ========================================================================== */

(function () {
  'use strict';

  const VYORO = (window.VYORO = window.VYORO || {});
  VYORO.settings = VYORO.settings || {};
  VYORO.routes = VYORO.routes || {};
  VYORO.strings = VYORO.strings || {};

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ------------------------------------------------------------------------
     Utilities
     ---------------------------------------------------------------------- */

  const utils = {
    prefersReducedMotion() {
      return prefersReducedMotion.matches;
    },

    debounce(fn, wait) {
      let timer;
      return function debounced(...args) {
        clearTimeout(timer);
        timer = setTimeout(() => fn.apply(this, args), wait);
      };
    },

    /**
     * Format a price in the shop's currency.
     * Amount is expected in the shop's minor units (cents/paise), matching
     * Shopify's JSON APIs.
     */
    formatMoney(cents) {
      const amount = (Number(cents) || 0) / 100;
      const format = VYORO.settings.moneyFormat || '{{amount}}';

      // Derive the currency symbol/wrapper from the shop's money format so we
      // stay consistent with the store's own presentation.
      const formatted = amount.toLocaleString(document.documentElement.lang || 'en', {
        minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
        maximumFractionDigits: 2
      });

      return format
        .replace(/\{\{\s*amount(_no_decimals|_with_comma_separator|_no_decimals_with_comma_separator|_with_apostrophe_separator|_no_decimals_with_space_separator|_with_space_separator|_with_period_and_space_separator)?\s*\}\}/g, formatted)
        .replace(/<[^>]*>/g, '');
    },

    announce(message) {
      const region = document.getElementById('LiveRegion');
      if (!region || !message) return;
      region.textContent = '';
      // Re-assign on the next frame so screen readers register the change.
      window.requestAnimationFrame(() => {
        region.textContent = message;
      });
    },

    /**
     * Fetch a rendered section from the Section Rendering API.
     */
    async fetchSection(sectionId, url) {
      const target = new URL(url || window.location.href, window.location.origin);
      target.searchParams.set('section_id', sectionId);
      const response = await fetch(target.toString());
      if (!response.ok) throw new Error(`Section request failed: ${response.status}`);
      return response.text();
    },

    parseHTML(html) {
      return new DOMParser().parseFromString(html, 'text/html');
    },

    /**
     * Replace the inner content of `target` with the matching node from
     * freshly rendered markup.
     */
    replaceContent(target, sourceHTML, selector) {
      const doc = utils.parseHTML(sourceHTML);
      const source = doc.querySelector(selector);
      if (!target || !source) return false;
      target.innerHTML = source.innerHTML;
      return true;
    }
  };

  VYORO.utils = utils;

  /* ------------------------------------------------------------------------
     Focus trap
     ---------------------------------------------------------------------- */

  const FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(', ');

  class FocusTrap {
    constructor(container) {
      this.container = container;
      this.handleKeydown = this.handleKeydown.bind(this);
    }

    getFocusable() {
      return Array.from(this.container.querySelectorAll(FOCUSABLE)).filter((el) => {
        return el.offsetParent !== null || el === document.activeElement;
      });
    }

    activate() {
      this.previouslyFocused = document.activeElement;
      document.addEventListener('keydown', this.handleKeydown);
      const focusable = this.getFocusable();
      const initial = this.container.querySelector('[data-focus-first]') || focusable[0];
      if (initial) {
        window.requestAnimationFrame(() => initial.focus());
      }
    }

    deactivate() {
      document.removeEventListener('keydown', this.handleKeydown);
      if (this.previouslyFocused && typeof this.previouslyFocused.focus === 'function') {
        this.previouslyFocused.focus();
      }
    }

    handleKeydown(event) {
      if (event.key !== 'Tab') return;
      const focusable = this.getFocusable();
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  VYORO.FocusTrap = FocusTrap;

  /* ------------------------------------------------------------------------
     Overlay controller
     Shared open/close/scroll-lock/focus behaviour for the mobile navigation,
     search overlay, cart drawer and mobile filter sheet.
     ---------------------------------------------------------------------- */

  const openOverlays = new Set();

  class Overlay {
    constructor(element) {
      this.element = element;
      this.trap = new FocusTrap(element);
      this.isOpen = false;
      this.onKeydown = this.onKeydown.bind(this);

      element.addEventListener('click', (event) => {
        if (event.target.closest('[data-overlay-close]')) {
          event.preventDefault();
          this.close();
        }
      });
    }

    open(trigger) {
      if (this.isOpen) return;
      this.isOpen = true;
      this.trigger = trigger || null;
      this.element.classList.add('is-open');
      this.element.removeAttribute('aria-hidden');
      openOverlays.add(this);
      document.body.classList.add('overflow-hidden');
      document.addEventListener('keydown', this.onKeydown);
      this.trap.activate();
      if (this.trigger) this.trigger.setAttribute('aria-expanded', 'true');
      this.element.dispatchEvent(new CustomEvent('overlay:open', { bubbles: true }));
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.element.classList.remove('is-open');
      this.element.setAttribute('aria-hidden', 'true');
      openOverlays.delete(this);
      if (!openOverlays.size) document.body.classList.remove('overflow-hidden');
      document.removeEventListener('keydown', this.onKeydown);
      this.trap.deactivate();
      if (this.trigger) this.trigger.setAttribute('aria-expanded', 'false');
      this.element.dispatchEvent(new CustomEvent('overlay:close', { bubbles: true }));
    }

    toggle(trigger) {
      this.isOpen ? this.close() : this.open(trigger);
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.stopPropagation();
        this.close();
      }
    }
  }

  VYORO.Overlay = Overlay;

  const overlayRegistry = new Map();

  VYORO.getOverlay = function getOverlay(id) {
    if (overlayRegistry.has(id)) return overlayRegistry.get(id);
    const element = document.getElementById(id);
    if (!element) return null;
    const overlay = new Overlay(element);
    overlayRegistry.set(id, overlay);
    return overlay;
  };

  // Global delegated triggers: [data-overlay-open="OverlayId"]
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-overlay-open]');
    if (!trigger) return;
    const overlay = VYORO.getOverlay(trigger.getAttribute('data-overlay-open'));
    if (!overlay) return;
    event.preventDefault();
    overlay.toggle(trigger);
  });

  /* ------------------------------------------------------------------------
     Header
     ---------------------------------------------------------------------- */

  function initHeader() {
    const header = document.querySelector('[data-header]');
    if (!header) return;

    const setHeaderHeight = () => {
      const height = header.getBoundingClientRect().height;
      document.documentElement.style.setProperty('--header-height', `${Math.round(height)}px`);
    };

    setHeaderHeight();
    window.addEventListener('resize', utils.debounce(setHeaderHeight, 150));

    if (!VYORO.settings.stickyHeader) return;

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        header.classList.toggle('is-stuck', window.scrollY > 8);
        setHeaderHeight();
        ticking = false;
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ------------------------------------------------------------------------
     Mobile navigation — collapsible submenus
     ---------------------------------------------------------------------- */

  function initMobileNav() {
    document.addEventListener('click', (event) => {
      const toggle = event.target.closest('[data-mobile-submenu-toggle]');
      if (!toggle) return;

      event.preventDefault();
      const submenu = document.getElementById(toggle.getAttribute('aria-controls'));
      if (!submenu) return;

      const expanded = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!expanded));
      submenu.classList.toggle('is-open', !expanded);
    });
  }

  /* ------------------------------------------------------------------------
     Reveal animations
     ---------------------------------------------------------------------- */

  function initReveal() {
    const targets = document.querySelectorAll('.reveal, .reveal--stagger');
    if (!targets.length) return;

    if (!VYORO.settings.revealAnimations || utils.prefersReducedMotion() || !('IntersectionObserver' in window)) {
      targets.forEach((el) => el.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.05 }
    );

    targets.forEach((el) => observer.observe(el));
  }

  /* ------------------------------------------------------------------------
     Number counters
     ---------------------------------------------------------------------- */

  function initCounters() {
    const counters = document.querySelectorAll('[data-counter]');
    if (!counters.length) return;

    const run = (el) => {
      const target = parseFloat(el.getAttribute('data-counter'));
      if (Number.isNaN(target)) return;

      const decimals = (el.getAttribute('data-counter-decimals') || '0') | 0;
      const prefix = el.getAttribute('data-counter-prefix') || '';
      const suffix = el.getAttribute('data-counter-suffix') || '';

      if (utils.prefersReducedMotion()) {
        el.textContent = prefix + target.toFixed(decimals) + suffix;
        return;
      }

      const duration = 900;
      const start = performance.now();

      const tick = (now) => {
        const progress = Math.min((now - start) / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        el.textContent = prefix + (target * eased).toFixed(decimals) + suffix;
        if (progress < 1) window.requestAnimationFrame(tick);
      };

      window.requestAnimationFrame(tick);
    };

    if (!('IntersectionObserver' in window)) {
      counters.forEach(run);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          run(entry.target);
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.4 }
    );

    counters.forEach((el) => observer.observe(el));
  }

  /* ------------------------------------------------------------------------
     Accordions
     ---------------------------------------------------------------------- */

  function initAccordions() {
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-accordion-trigger]');
      if (!trigger) return;

      const panel = document.getElementById(trigger.getAttribute('aria-controls'));
      if (!panel) return;

      const expanded = trigger.getAttribute('aria-expanded') === 'true';
      const group = trigger.closest('[data-accordion-single]');

      if (group && !expanded) {
        group.querySelectorAll('[data-accordion-trigger][aria-expanded="true"]').forEach((other) => {
          const otherPanel = document.getElementById(other.getAttribute('aria-controls'));
          other.setAttribute('aria-expanded', 'false');
          if (otherPanel) otherPanel.hidden = true;
        });
      }

      trigger.setAttribute('aria-expanded', String(!expanded));
      panel.hidden = expanded;
    });
  }

  /* ------------------------------------------------------------------------
     Tabs
     ---------------------------------------------------------------------- */

  function initTabs() {
    document.querySelectorAll('[data-tabs]').forEach((group) => {
      const tabs = Array.from(group.querySelectorAll('[role="tab"]'));
      if (!tabs.length) return;

      const activate = (tab) => {
        tabs.forEach((other) => {
          const panel = document.getElementById(other.getAttribute('aria-controls'));
          const selected = other === tab;
          other.setAttribute('aria-selected', String(selected));
          other.setAttribute('tabindex', selected ? '0' : '-1');
          if (panel) panel.hidden = !selected;
        });
      };

      group.addEventListener('click', (event) => {
        const tab = event.target.closest('[role="tab"]');
        if (tab) activate(tab);
      });

      group.addEventListener('keydown', (event) => {
        const currentIndex = tabs.indexOf(document.activeElement);
        if (currentIndex === -1) return;

        let nextIndex = null;
        if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabs.length;
        if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = tabs.length - 1;

        if (nextIndex === null) return;
        event.preventDefault();
        tabs[nextIndex].focus();
        activate(tabs[nextIndex]);
      });
    });
  }

  /* ------------------------------------------------------------------------
     Quantity steppers
     Emits `quantity:change` so pricing modules can react.
     ---------------------------------------------------------------------- */

  function clampQuantity(input) {
    const min = parseInt(input.getAttribute('min'), 10) || 1;
    const step = parseInt(input.getAttribute('step'), 10) || 1;
    const maxAttr = input.getAttribute('max');
    const max = maxAttr ? parseInt(maxAttr, 10) : null;

    let value = parseInt(input.value, 10);
    if (Number.isNaN(value)) value = min;

    // Snap to the nearest valid increment above the minimum.
    if (step > 1) {
      const steps = Math.ceil((value - min) / step);
      value = min + Math.max(0, steps) * step;
    }

    if (value < min) value = min;
    if (max !== null && value > max) value = max;

    input.value = value;
    return value;
  }

  function initQuantity() {
    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-quantity-button]');
      if (!button) return;

      const wrapper = button.closest('[data-quantity]');
      const input = wrapper && wrapper.querySelector('[data-quantity-input]');
      if (!input) return;

      event.preventDefault();

      const step = parseInt(input.getAttribute('step'), 10) || 1;
      const direction = button.getAttribute('data-quantity-button') === 'increase' ? 1 : -1;
      const current = parseInt(input.value, 10) || 0;

      input.value = current + direction * step;
      const value = clampQuantity(input);

      input.dispatchEvent(new CustomEvent('quantity:change', { bubbles: true, detail: { value } }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
      updateQuantityButtons(wrapper);
    });

    document.addEventListener('change', (event) => {
      const input = event.target.closest('[data-quantity-input]');
      if (!input) return;
      const value = clampQuantity(input);
      input.dispatchEvent(new CustomEvent('quantity:change', { bubbles: true, detail: { value } }));
      updateQuantityButtons(input.closest('[data-quantity]'));
    });

    document.querySelectorAll('[data-quantity]').forEach(updateQuantityButtons);
  }

  function updateQuantityButtons(wrapper) {
    if (!wrapper) return;
    const input = wrapper.querySelector('[data-quantity-input]');
    if (!input) return;

    const value = parseInt(input.value, 10) || 0;
    const min = parseInt(input.getAttribute('min'), 10) || 1;
    const maxAttr = input.getAttribute('max');
    const max = maxAttr ? parseInt(maxAttr, 10) : null;

    const decrease = wrapper.querySelector('[data-quantity-button="decrease"]');
    const increase = wrapper.querySelector('[data-quantity-button="increase"]');

    if (decrease) decrease.disabled = value <= min;
    if (increase) increase.disabled = max !== null && value >= max;
  }

  VYORO.updateQuantityButtons = updateQuantityButtons;
  VYORO.clampQuantity = clampQuantity;

  /* ------------------------------------------------------------------------
     Boot
     ---------------------------------------------------------------------- */

  function init() {
    initHeader();
    initMobileNav();
    initReveal();
    initCounters();
    initAccordions();
    initTabs();
    initQuantity();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-initialise when a section is re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', () => {
    initReveal();
    initCounters();
    initTabs();
    document.querySelectorAll('[data-quantity]').forEach(updateQuantityButtons);
  });
})();
